import React, { useMemo } from "react";
import { useForm, useWatch } from "react-hook-form";
import { ReactSelectField } from "../../components/reactHookFormFields/selectField";
import { TextAreaField } from "../../components/reactHookFormFields/textAreaFields";
import { useGroupChatContext } from "../../context/groupchatContext";
import { PageDialogContent, PageDialogFooter } from "../../components/dialog";
import { BtnComponent } from "../../components/button";
import SpeechMic from "../../components/mic";
import { CheckBox } from "../../components/reactHookFormFields/checkbox";
import { useAddTriageNoteMutation, useGetTriageNoteMetaQuery } from "../../services/triageServices";
import { FaUsers } from "react-icons/fa";
import { toast } from "react-toastify";
import { PatientSearchField } from "../../components/reactHookFormFields/patientSearchField";
import { removeHtmlTags } from "../../helpers/utility";

export const TriageNotePopup = ({ closeTriagePopup, data }) => {
    
    const groupChatContext = useGroupChatContext();
    const { authData, selectedChat, selectedChatTab } = useMemo(() => ({
        authData: groupChatContext.authData,
        selectedChat: groupChatContext.selectedChat,
        selectedChatTab: groupChatContext.selectedChatTab
    }));

    const {data: triageNoteMeta} = useGetTriageNoteMetaQuery();

    const {
        control,
        reset,
        handleSubmit,
        formState: { errors },
        getValues,
        setValue
    } = useForm({defaultValues: {notes: removeHtmlTags(data?.text)}});

    const notesVal  = useWatch({control, name: 'notes'})

    const handleClosePopup = () => {
        reset()
        closeTriagePopup()
    }

    const [addTriageNote, {isLoading}] = useAddTriageNoteMutation();
    const onSubmitForm = async(data) => {

        const payload = {
            pids: selectedChatTab == 'location_chats' ? data.patient?.pid : selectedChat?.pid,
            assign_to: data.assign_to_users?.filter(item => item.type != 'group')?.map(item => item.id)?.join(','),
            group_id: data.assign_to_users?.filter(item => item.type == 'group')?.map(item => item.id)?.join(','),
            priority: data.priority?.option_id,
            notes: data.notes,
            status: data.mark_as_resolved ? 'resolved' : ''
        }

        const res = await addTriageNote(payload);
        if(res?.data?.code == 0){
            handleClosePopup();
            toast.success(res.data?.msg);
        }
    };

    const onSpeechAvailable = (text) => {
        const commentsVal = getValues('notes');
        const finalText = ((commentsVal ? commentsVal : '')+' '+text).trim()
        setValue('notes', finalText)
    }


    return (
        <>
            <PageDialogContent contentCls="p-3 sm:p-4 md:px-6">
                <form id="triage_note_form" onSubmit={handleSubmit(onSubmitForm)} className="min-w-0">
                    <div className="grid grid-cols-1 md:grid-cols-6 gap-3 md:gap-4">
                        {selectedChatTab == 'location_chats' ? (
                            <div className="md:col-span-2 min-w-0">
                                <PatientSearchField
                                    title="Patient Name"
                                    name="patient"
                                    location={selectedChatTab == 'location_chats' ? selectedChat?.location_id : ''}
                                    control={control}
                                    rules={{
                                        required: {
                                            value: true,
                                            message: 'Patient is required'
                                        },
                                    }}
                                    errors={errors}
                                />
                            </div>
                        ) : null}
                        <div className={`min-w-0 ${selectedChatTab == 'location_chats' ? 'md:col-span-3' : 'md:col-span-5'}`}>
                            <ReactSelectField
                                title="Assigned To"
                                name="assign_to_users"
                                control={control}
                                isMulti={true}
                                optionData={triageNoteMeta?.task_group ? [...triageNoteMeta?.task_group, ...triageNoteMeta?.provider_list].map((v) => {
                                    return { ...v, label: v.name || v.title };
                                  }) : null}
                                menuPlacement={"bottom"}
                                getOptionLabel={ item => {
                                    if(item?.type == 'group'){
                                        return (
                                            <div className="flex items-center">
                                                <FaUsers size="20" /> <span className="ml-2">{item.title}</span>
                                            </div>
                                        )
                                    }
                                    return item?.name;
                                }}
                            />
                        </div>
                        <div className="md:col-span-1 min-w-0">
                            <ReactSelectField
                                title="Priority"
                                name="priority"
                                control={control}
                                optionData={triageNoteMeta?.priority?.map(item => ({...item, label: item.title}))}
                                menuPlacement={"bottom"}
                                getOptionLabel={ item => item?.title }
                            />
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-6 gap-3 md:gap-4 mt-3">
                        <div className="relative md:col-span-5 min-w-0">
                            <SpeechMic transformSpeech={onSpeechAvailable} />
                            <TextAreaField
                                title="Notes"
                                rows="4"
                                name="notes"
                                control={control}
                                rules={{
                                    required: {
                                        value: true,
                                        message: 'Notes is required'
                                    },
                                }}
                                errors={errors}
                            />
                        </div>
                        <div className="md:col-span-1 min-w-0 flex items-end pb-1 md:pb-0">
                            <CheckBox
                                title="Mark as Resolved"
                                name="mark_as_resolved"
                                disabled={!notesVal?.length}
                                control={control}
                            />
                        </div>
                    </div>
                </form>
            </PageDialogContent>
            <PageDialogFooter>
                <BtnComponent text="Cancel" btnType="default" onClick={handleClosePopup} />
                <BtnComponent text="Save" btnType={authData.is_provider ? 'primary' : 'primary_theme'} loading={isLoading} form="triage_note_form" type="submit" />
            </PageDialogFooter>
        </>
    );
};